import { useState } from 'react';
import { ArrowDownRight, ArrowUpRight, IndianRupee, ListChecks, Minus, Receipt } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { SegmentedControl } from '@/components/ui/segmented';
import { StatCard } from '@/components/ui/stat-card';
import { StatusPill } from '@/components/ui/status-pill';
import { Skeleton } from '@/components/ui/skeleton';
import { use_reports } from '@/features/vendor-stats/hooks';
import type { ReportRange, VendorReports } from '@/features/vendor-stats/types';
import { cn, format_inr } from '@/lib/utils';

const RANGE_OPTIONS: { value: ReportRange; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '12m', label: '12 months' },
];

const SOURCE_LABELS: Record<string, string> = {
  bookings_inr: 'Bookings',
  orders_inr: 'Dining orders',
  shipments_inr: 'Shipments',
};

const STATUS_TONE: Record<string, 'success' | 'warning' | 'destructive' | 'neutral'> = {
  COMPLETED: 'success',
  CONFIRMED: 'neutral',
  IN_PROGRESS: 'neutral',
  AWAITING_VENDOR: 'warning',
  PENDING_PAYMENT: 'warning',
  CANCELLED: 'destructive',
  NO_SHOW: 'destructive',
};

/** Signed growth chip. `null` (no prior baseline) renders as a muted "New". */
function GrowthPill({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="text-xs font-medium text-muted-foreground">New this period</span>;
  const up = pct > 0;
  const flat = pct === 0;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-semibold',
        flat ? 'text-muted-foreground' : up ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {up ? '+' : ''}
      {pct}% vs prev
    </span>
  );
}

function RevenueChart({ report }: { report: VendorReports }) {
  const max = Math.max(1, ...report.series.map((p) => Number(p.revenue_inr)));
  const fmt_label = (bucket: string) => {
    const d = new Date(`${report.granularity === 'month' ? `${bucket}-01` : bucket}T00:00:00`);
    return report.granularity === 'month'
      ? d.toLocaleDateString('en-IN', { month: 'short' })
      : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };
  // Keep the axis readable: show ~6 labels regardless of bucket count.
  const label_every = Math.max(1, Math.ceil(report.series.length / 6));
  const has_any = report.series.some((p) => Number(p.revenue_inr) > 0);

  return (
    <div data-testid="revenue-chart">
      <div className="flex h-48 items-end gap-[3px]">
        {report.series.map((p, i) => {
          const v = Number(p.revenue_inr);
          const h = Math.round((v / max) * 100);
          return (
            <div key={p.bucket} className="group relative flex flex-1 flex-col items-center justify-end">
              <div
                className="w-full rounded-t-[4px] bg-primary/80 transition-colors group-hover:bg-primary"
                style={{ height: `${Math.max(v > 0 ? 4 : 0, h)}%` }}
                title={`${fmt_label(p.bucket)}: ${format_inr(p.revenue_inr)} · ${p.jobs} job${p.jobs === 1 ? '' : 's'}`}
              />
              {i % label_every === 0 && (
                <span className="mt-1.5 whitespace-nowrap text-[10px] leading-none text-muted-foreground">
                  {fmt_label(p.bucket)}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {!has_any && (
        <p className="mt-4 text-center text-sm text-muted-foreground">No earnings in this period yet.</p>
      )}
    </div>
  );
}

function BreakdownBars({ report }: { report: VendorReports }) {
  const rows = report.by_module.length
    ? report.by_module.map((m) => ({ label: m.module, value: Number(m.revenue_inr), jobs: m.jobs }))
    : (Object.entries(report.by_source) as [string, string][])
        .map(([k, v]) => ({ label: SOURCE_LABELS[k] ?? k, value: Number(v), jobs: 0 }))
        .filter((r) => r.value > 0);
  const total = rows.reduce((s, r) => s + r.value, 0);
  if (total === 0) return <p className="text-sm text-muted-foreground">No revenue to break down yet.</p>;

  return (
    <div className="flex flex-col gap-3">
      {rows
        .sort((a, b) => b.value - a.value)
        .map((r) => {
          const pct = Math.round((r.value / total) * 100);
          return (
            <div key={r.label}>
              <div className="mb-1 flex items-center justify-between text-sm">
                <span className="font-medium">{r.label}</span>
                <span className="tabular-nums text-muted-foreground">
                  {format_inr(r.value)} · {pct}%
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-secondary">
                <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
    </div>
  );
}

export function VendorReportsPage() {
  const [range, setRange] = useState<ReportRange>('30d');
  const reports_q = use_reports(range);
  const report = reports_q.data;

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader title="Reports & Analytics" description="Track revenue, jobs and growth over time." />

      <div className="max-w-md">
        <SegmentedControl
          value={range}
          onChange={setRange}
          options={RANGE_OPTIONS}
          testId="reports-range"
          aria-label="Report range"
        />
      </div>

      {reports_q.isLoading || !report ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-[104px] rounded-card" />
            ))}
          </div>
          <Skeleton className="h-64 rounded-card" />
        </>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="reports-kpis">
            <StatCard
              label="Revenue"
              value={format_inr(report.totals.revenue_inr)}
              icon={IndianRupee}
              hint={<GrowthPill pct={report.growth.revenue_pct} />}
            />
            <StatCard
              label="Completed jobs"
              value={report.totals.completed_jobs}
              icon={ListChecks}
              hint={<GrowthPill pct={report.growth.jobs_pct} />}
            />
            <StatCard
              label="Avg. value"
              value={format_inr(report.totals.avg_value_inr)}
              icon={Receipt}
              hint="Per completed job"
            />
          </div>

          {/* Revenue trend */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Revenue trend</CardTitle>
            </CardHeader>
            <CardContent>
              <RevenueChart report={report} />
            </CardContent>
          </Card>

          {/* Breakdown + booking status */}
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Revenue breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <BreakdownBars report={report} />
              </CardContent>
            </Card>

            {Object.keys(report.booking_status).length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Booking outcomes</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {Object.entries(report.booking_status)
                    .sort((a, b) => b[1] - a[1])
                    .map(([status, count]) => (
                      <StatusPill key={status} tone={STATUS_TONE[status] ?? 'neutral'}>
                        {status.replace(/_/g, ' ')}: {count}
                      </StatusPill>
                    ))}
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
